import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { RestService } from './rest.service';
import { BrandService } from '../../services/brand-config.service';
import { TranslationService } from './translation.service';

/** Which benefit a clinic's patients get. Mutually exclusive by construction:
 *  'none' = they pay as usual, 'full' = patientsFreeAccess, 'tests' = N free tests. */
export type ClinicPatientBenefit = 'none' | 'full' | 'tests';

export interface ClinicPublicConfig {
  id: number;
  slug: string;
  nameEn: string | null;
  nameHe: string | null;
  language: 'en' | 'he';
  brand: 'peritale' | 'markable';
  primaryColor: string | null;
  hasLogo: boolean;
  hasFavicon: boolean;
  assetVersion: number;
  allowDoctorSelfSignup: boolean;
  patientsFreeAccess: boolean;
  /** Free tests every patient of this clinic gets (0 = none). */
  patientFreeTests: number;
  features: Record<string, any>;
  status: 'active' | 'suspended';
  created?: string;
  updated?: string;
}

export interface ClinicMembership {
  id: number;
  role: 'admin' | 'doctor';
  status: 'active' | 'blocked';
  clinic: ClinicPublicConfig;
}

export interface ClinicMemberRow {
  id: number;
  userId: number;
  email: string;
  name: string;
  credential: string | null;
  role: 'admin' | 'doctor';
  status: 'active' | 'blocked';
  joinedAt: string | null;
  created: string;
  patientCount?: number;
}

export interface ClinicPatientRow {
  id: number;
  userId: number;
  email: string;
  name: string;
  source: string;
  consentAt: string | null;
  joined: string;
  doctors: {
    linkId: number;
    doctorUserId: number;
    doctorName: string;
    doctorEmail: string;
    clinicId: number | null;
    since: string;
  }[];
}

export interface AuditRow {
  id: number;
  at: string;
  actorUserId: number | null;
  actorEmail: string | null;
  actorKind: string;
  actingAs: string | null;
  clinicId: number | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  before: any;
  after: any;
  ip: string | null;
  userAgent: string | null;
  requestId: string | null;
}

/** The three patient-portal consent checkboxes as ticked, with the shown text. */
export interface ClinicConsentDetail {
  cb1: boolean;
  cb2: boolean;
  cb3: boolean;
  texts?: { [key: string]: string };
  locale?: string | null;
}

/** What a patient agreed to while logging in through a clinic page. Consumed once after auth. */
export interface PendingClinicJoin {
  slug: string;
  invite?: string | null;
  consent: boolean;
  consentDetail?: ClinicConsentDetail | null;
  at: number;
}

const PENDING_KEY = 'clinic:pendingJoin';
const MEMBER_INVITE_KEY = 'clinic:memberInvite';

// Brand host per environment (D1: a clinic lives in one brand; its URLs on the
// other brand's host redirect here). Staging hosts are the canonical ones from
// CLAUDE.md; prod peritale is assumed app.peritale.online.
const BRAND_HOSTS: Record<string, Record<'markable' | 'peritale', string>> = {
  Staging: {
    markable: 'app.staging.markable.life',
    peritale: 'app.staging.peritale.online',
  },
  Prod: {
    markable: 'app.markable.life',
    peritale: 'app.peritale.online',
  },
};

@Injectable({ providedIn: 'root' })
export class ClinicService {
  private readonly baseUrl: string = environment.apiUrl;
  private readonly cache = new Map<string, Promise<ClinicPublicConfig | null>>();
  /** The clinic whose page the visitor is on (login / doctor landing / join). */
  readonly current$ = new BehaviorSubject<ClinicPublicConfig | null>(null);

  constructor(
    private rest: RestService,
    private brand: BrandService,
    private translation: TranslationService,
  ) {}

  get current(): ClinicPublicConfig | null {
    return this.current$.value;
  }

  is(slug: string): boolean {
    return this.current?.slug === slug;
  }

  // ─── public config + branding ─────────────────────────────────────────────

  async loadPublic(slug: string): Promise<ClinicPublicConfig | null> {
    const s = String(slug || '').toLowerCase();
    if (!s) return null;
    if (!this.cache.has(s)) {
      this.cache.set(
        s,
        (this.rest
          .request<ClinicPublicConfig>({
            method: 'GET',
            path: `clinic/public/${encodeURIComponent(s)}`,
            throwError: true,
            skipAuthRedirect: true,
          })
          .catch(() => null) as Promise<ClinicPublicConfig | null>),
      );
    }
    return this.cache.get(s) as Promise<ClinicPublicConfig | null>;
  }

  invalidate(slug: string): void {
    this.cache.delete(String(slug || '').toLowerCase());
  }

  /** Load + set as the current clinic + apply favicon/color. Returns null if unknown. */
  async enter(slug: string): Promise<ClinicPublicConfig | null> {
    const cfg = await this.loadPublic(slug);
    this.current$.next(cfg);
    if (cfg) this.applyBranding(cfg);
    return cfg;
  }

  leave(): void {
    this.current$.next(null);
    document.documentElement.style.removeProperty('--clinic-color');
  }

  logoUrl(cfg: Pick<ClinicPublicConfig, 'slug' | 'hasLogo' | 'assetVersion'> | null): string {
    if (!cfg?.hasLogo) return '';
    return new URL(
      `clinic/public/${encodeURIComponent(cfg.slug)}/logo?v=${cfg.assetVersion || 0}`,
      this.baseUrl,
    ).toString();
  }

  faviconUrl(cfg: ClinicPublicConfig | null): string {
    if (!cfg?.hasFavicon) return '';
    return new URL(
      `clinic/public/${encodeURIComponent(cfg.slug)}/favicon?v=${cfg.assetVersion || 0}`,
      this.baseUrl,
    ).toString();
  }

  displayName(cfg: Pick<ClinicPublicConfig, 'nameEn' | 'nameHe'> | null): string {
    if (!cfg) return '';
    const he = this.translation.getCurrentLanguage() === 'he';
    return (he ? cfg.nameHe || cfg.nameEn : cfg.nameEn || cfg.nameHe) || '';
  }

  applyBranding(cfg: ClinicPublicConfig): void {
    try {
      if (cfg.primaryColor)
        document.documentElement.style.setProperty('--clinic-color', cfg.primaryColor);
      const fav = this.faviconUrl(cfg);
      if (fav) {
        let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = fav;
      }
    } catch {
      /* noop */
    }
  }

  /**
   * A clinic lives in one brand. If this page is served by the other brand's
   * host, jump to the right host with the same path. Returns true when a
   * redirect was issued (caller should stop).
   */
  ensureBrandHost(cfg: ClinicPublicConfig | null): boolean {
    if (!cfg) return false;
    const here = this.brand.config.isMarkable ? 'markable' : 'peritale';
    if (cfg.brand === here) return false;
    const hosts = BRAND_HOSTS[environment.env_name];
    if (!hosts) return false; // local dev: stay put (brand flips via ?brand=)
    const target = hosts[cfg.brand];
    if (!target || window.location.host === target) return false;
    window.location.replace(
      `https://${target}${window.location.pathname}${window.location.search}`,
    );
    return true;
  }

  // ─── pending join (survives the auth round trip) ─────────────────────────

  setPending(p: Omit<PendingClinicJoin, 'at'>): void {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify({ ...p, at: Date.now() }));
    } catch {
      /* noop */
    }
  }

  peekPending(): PendingClinicJoin | null {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return null;
      const p = JSON.parse(raw) as PendingClinicJoin;
      // Stale after a day (she abandoned the flow).
      if (!p?.slug || Date.now() - (p.at || 0) > 24 * 60 * 60 * 1000) {
        this.clearPending();
        return null;
      }
      return p;
    } catch {
      return null;
    }
  }

  consumePending(): PendingClinicJoin | null {
    const p = this.peekPending();
    if (p) this.clearPending();
    return p;
  }

  clearPending(): void {
    try {
      localStorage.removeItem(PENDING_KEY);
    } catch {
      /* noop */
    }
  }

  setPendingMemberInvite(token: string | null): void {
    try {
      if (token) sessionStorage.setItem(MEMBER_INVITE_KEY, token);
      else sessionStorage.removeItem(MEMBER_INVITE_KEY);
    } catch {
      /* noop */
    }
  }

  getPendingMemberInvite(): string | null {
    try {
      return sessionStorage.getItem(MEMBER_INVITE_KEY);
    } catch {
      return null;
    }
  }

  /** Runs the pending join/accept after login. Null when nothing was pending. */
  async settlePending(): Promise<{
    kind: 'invite' | 'join' | 'failed';
    response: any;
    pending: PendingClinicJoin;
  } | null> {
    const p = this.consumePending();
    if (!p) return null;
    try {
      if (p.invite) {
        const response = await this.acceptInvite(p.invite, p.consent);
        return { kind: 'invite', response, pending: p };
      }
      const response = await this.patientJoin(
        p.slug,
        p.consent,
        p.consentDetail ?? null,
      );
      return { kind: 'join', response, pending: p };
    } catch (e) {
      console.error('ClinicService: pending join failed', e);
      return { kind: 'failed', response: null, pending: p };
    }
  }

  // ─── patient endpoints ───────────────────────────────────────────────────

  getInvitePreview(token: string): Promise<any> {
    return this.rest.request({
      method: 'GET',
      path: `maccabi/invitations/${encodeURIComponent(token)}`,
      throwError: true,
      skipAuthRedirect: true,
    });
  }

  acceptInvite(token: string, consent: boolean): Promise<any> {
    return this.rest.request({
      method: 'POST',
      path: `clinic/invite/${encodeURIComponent(token)}/accept`,
      body: { consent },
      throwError: true,
    });
  }

  patientJoin(
    slug: string,
    consent: boolean,
    consentDetail: ClinicConsentDetail | null = null,
  ): Promise<any> {
    return this.rest.request({
      method: 'POST',
      path: `clinic/${encodeURIComponent(slug)}/patient/join`,
      body: { consent, consentDetail },
      throwError: true,
    });
  }

  // ─── doctor endpoints ────────────────────────────────────────────────────

  mine(): Promise<{ memberships: ClinicMembership[] }> {
    return this.rest.request({
      method: 'GET',
      path: 'clinic/mine',
      throwError: true,
    }) as Promise<{ memberships: ClinicMembership[] }>;
  }

  join(slug: string, token?: string | null): Promise<any> {
    return this.rest.request({
      method: 'POST',
      path: `clinic/${encodeURIComponent(slug)}/join`,
      body: { token: token || null },
      throwError: true,
    });
  }

  getMemberInvite(token: string): Promise<any> {
    return this.rest.request({
      method: 'GET',
      path: `clinic/member-invite/${encodeURIComponent(token)}`,
      throwError: true,
      skipAuthRedirect: true,
    });
  }

  create(body: any): Promise<ClinicPublicConfig> {
    return this.rest.request({
      method: 'POST',
      path: 'clinic',
      body,
      throwError: true,
    }) as Promise<ClinicPublicConfig>;
  }

  slugAvailable(slug: string): Promise<{ slug: string; available: boolean }> {
    return this.rest.request({
      method: 'GET',
      path: `clinic/slug-available/${encodeURIComponent(slug)}`,
      throwError: true,
      skipAuthRedirect: true,
    }) as Promise<{ slug: string; available: boolean }>;
  }

  // ─── clinic admin endpoints ──────────────────────────────────────────────

  getSettings(slug: string): Promise<ClinicPublicConfig> {
    return this.rest.request({
      method: 'GET',
      path: `clinic/${encodeURIComponent(slug)}`,
      throwError: true,
      skipAuthRedirect: true,
    }) as Promise<ClinicPublicConfig>;
  }

  updateSettings(slug: string, body: any): Promise<ClinicPublicConfig> {
    this.invalidate(slug);
    return this.rest.request({
      method: 'PUT',
      path: `clinic/${encodeURIComponent(slug)}`,
      body,
      throwError: true,
    }) as Promise<ClinicPublicConfig>;
  }

  setAsset(slug: string, kind: 'logo' | 'favicon', dataUrl: string | null): Promise<ClinicPublicConfig> {
    this.invalidate(slug);
    return this.rest.request({
      method: 'POST',
      path: `clinic/${encodeURIComponent(slug)}/asset`,
      body: { kind, dataUrl },
      throwError: true,
    }) as Promise<ClinicPublicConfig>;
  }

  members(slug: string): Promise<{ members: ClinicMemberRow[]; pendingInvites: any[] }> {
    return this.rest.request({
      method: 'GET',
      path: `clinic/${encodeURIComponent(slug)}/members`,
      throwError: true,
      skipAuthRedirect: true,
    }) as Promise<{ members: ClinicMemberRow[]; pendingInvites: any[] }>;
  }

  inviteMember(slug: string, email: string, role: 'admin' | 'doctor', lang: string, sendEmail: boolean): Promise<any> {
    return this.rest.request({
      method: 'POST',
      path: `clinic/${encodeURIComponent(slug)}/members/invite`,
      body: { email, role, lang, sendEmail },
      throwError: true,
    });
  }

  addMember(
    slug: string,
    email: string,
    role: 'admin' | 'doctor',
    sendInvite = false,
    lang?: string,
  ): Promise<any> {
    return this.rest.request({
      method: 'POST',
      path: `clinic/${encodeURIComponent(slug)}/members`,
      body: { email, role, sendInvite, lang },
      throwError: true,
    });
  }

  /** Manually (re)send the invite / magic-login email to an existing member. */
  sendMemberInvite(slug: string, memberId: number, lang: string): Promise<any> {
    return this.rest.request({
      method: 'POST',
      path: `clinic/${encodeURIComponent(slug)}/members/${memberId}/send-invite`,
      body: { lang },
      throwError: true,
    });
  }

  updateMember(slug: string, memberId: number, body: { role?: string; status?: string }): Promise<any> {
    return this.rest.request({
      method: 'PUT',
      path: `clinic/${encodeURIComponent(slug)}/members/${memberId}`,
      body,
      throwError: true,
    });
  }

  removeMember(slug: string, memberId: number): Promise<any> {
    return this.rest.request({
      method: 'DELETE',
      path: `clinic/${encodeURIComponent(slug)}/members/${memberId}`,
      throwError: true,
    });
  }

  patients(slug: string): Promise<{ patients: ClinicPatientRow[] }> {
    return this.rest.request({
      method: 'GET',
      path: `clinic/${encodeURIComponent(slug)}/patients`,
      throwError: true,
      skipAuthRedirect: true,
    }) as Promise<{ patients: ClinicPatientRow[] }>;
  }

  attachPatient(slug: string, patientUserId: number, doctorUserId: number): Promise<any> {
    return this.rest.request({
      method: 'POST',
      path: `clinic/${encodeURIComponent(slug)}/patients/${patientUserId}/attach`,
      body: { doctorUserId },
      throwError: true,
    });
  }

  detachPatient(slug: string, patientUserId: number, linkId: number): Promise<any> {
    return this.rest.request({
      method: 'POST',
      path: `clinic/${encodeURIComponent(slug)}/patients/${patientUserId}/detach`,
      body: { linkId },
      throwError: true,
    });
  }

  removePatient(slug: string, patientUserId: number): Promise<any> {
    return this.rest.request({
      method: 'POST',
      path: `clinic/${encodeURIComponent(slug)}/patients/${patientUserId}/remove`,
      throwError: true,
    });
  }

  clinicAudit(slug: string, q: Record<string, any>): Promise<{ rows: AuditRow[] }> {
    return this.rest.request({
      method: 'GET',
      path: `clinic/${encodeURIComponent(slug)}/audit`,
      queryParams: this.cleanQuery(q),
      throwError: true,
      skipAuthRedirect: true,
    }) as Promise<{ rows: AuditRow[] }>;
  }

  // ─── system admin endpoints ──────────────────────────────────────────────

  adminList(): Promise<{ clinics: any[] }> {
    return this.rest.request({
      method: 'GET',
      path: 'admin/clinics',
      throwError: true,
    }) as Promise<{ clinics: any[] }>;
  }

  adminCreate(body: any): Promise<{ clinic: ClinicPublicConfig; invite: any }> {
    return this.rest.request({
      method: 'POST',
      path: 'admin/clinics',
      body,
      throwError: true,
    }) as Promise<{ clinic: ClinicPublicConfig; invite: any }>;
  }

  adminSetStatus(id: number, status: 'active' | 'suspended'): Promise<any> {
    return this.rest.request({
      method: 'PUT',
      path: `admin/clinics/${id}/status`,
      body: { status },
      throwError: true,
    });
  }

  adminAudit(q: Record<string, any>): Promise<{ rows: AuditRow[] }> {
    return this.rest.request({
      method: 'GET',
      path: 'admin/audit',
      queryParams: this.cleanQuery(q),
      throwError: true,
    }) as Promise<{ rows: AuditRow[] }>;
  }

  private cleanQuery(q: Record<string, any>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const k of Object.keys(q || {})) {
      const v = q[k];
      if (v === undefined || v === null || v === '') continue;
      out[k] = String(v);
    }
    return out;
  }
}
